import { saveBlob } from './_download';
import React, { useState, useCallback } from 'react';
import { Download, Printer } from 'lucide-react';
import { C, S } from './_overviewTheme';
import { buildReportModel, reportFilename } from './_reportModel';
import { captureOverviewCharts } from './_chartCapture';

/**
 * ⛔⛔ DOWNLOAD / PRINT — Card P156.
 *
 * Carlos prints the report to read and act on, and emails it. So:
 *
 *   · the four outputs share ONE model (_reportModel.js), built from the
 *     same props the screen renders — the spreadsheet cannot disagree with
 *     the dashboard it came from;
 *   · ⛔ summary level only, NO patient names, in any of them. Row-level
 *     detail stays behind analytics:drilldown:export, a separate permission;
 *   · ⛔ finance sections are OMITTED, not blanked, without finance:read;
 *   · every download is audit-logged with who, which format and which period.
 *
 * ⚠ The heavy builders are imported LAZILY, inside the click. docx, xlsx and
 * pdf-lib together are a large bundle, and a CEO who never exports should not
 * pay for them on every page load.
 */

// ⭐ P179 — moved to ./_download so MetricDrilldown can use the SAME save
// path. Its "Export CSV" had no save step at all and claimed success anyway.

export default function DownloadMenu({
  getModelArgs, rootRef, onAudit = () => {}, canFinance = false, disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');

  const run = useCallback(async (format) => {
    setBusy(format); setError('');
    try {
      const args = (typeof getModelArgs === 'function' ? getModelArgs() : {}) || {};
      const model = buildReportModel({ ...args, canFinance });

      /* ⚠ Charts only for the formats that can hold a picture. */
      const images = (format === 'pdf' || format === 'docx')
        ? await captureOverviewCharts(rootRef && rootRef.current)
        : {};

      if (format === 'xlsx') {
        const { xlsxBytes, xlsxFilename } = await import('./_reportXlsx');
        const bytes = xlsxBytes(model);
        saveBlob(new window.Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }), xlsxFilename(model));
      } else if (format === 'docx') {
        const { docxBlob, docxFilename } = await import('./_reportDocx');
        saveBlob(await docxBlob(model, images), docxFilename(model));
      } else if (format === 'pdf') {
        const { buildPdf, pdfFilename } = await import('./_reportPdf');
        const bytes = await buildPdf(model, images);
        saveBlob(new window.Blob([bytes], { type: 'application/pdf' }), pdfFilename(model));
      } else if (format === 'print') {
        window.print();
      }

      /*
       * ⚠ AUDITED AFTER THE FILE EXISTS, not before. Logging an export that
       * then failed would put a download in the record that never happened —
       * and this log is what an auditor reads to answer "who took the
       * numbers out".
       */
      onAudit({ format, period: model.period, filename: format === 'print' ? null : reportFilename(model, format) });
      setOpen(false);
    } catch (e) {
      setError('That download could not be produced. Nothing was sent anywhere.');
    } finally {
      setBusy(null);
    }
  }, [getModelArgs, rootRef, onAudit, canFinance]);

  const item = (format, label) => (
    <button
      key={format} type="button" role="menuitem"
      data-testid={`download-${format}`}
      disabled={busy !== null}
      onClick={() => run(format)}
      style={{
        display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
        fontSize: 12.5, border: 'none', background: 'none', cursor: busy ? 'default' : 'pointer',
        color: C.body,
      }}
    >{busy === format ? `${label}…` : label}</button>
  );

  return (
    <div style={{ position: 'relative' }} data-testid="download-menu">
      <button
        type="button" disabled={disabled}
        data-testid="download-menu-toggle"
        aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px',
          fontSize: 12, fontWeight: 600, borderRadius: 999, cursor: 'pointer',
          border: `1px solid ${C.border}`, background: C.card, color: C.body,
        }}
      >
        <Download size={13} aria-hidden="true" /> Download / Print
      </button>

      {open ? (
        <div role="menu" aria-label="Download or print this report"
             style={{
               position: 'absolute', right: 0, top: '100%', marginTop: 6, zIndex: 50,
               background: C.card, border: `1px solid ${C.border}`, borderRadius: 8,
               boxShadow: '0 8px 24px rgba(0,0,0,.12)', minWidth: 210, padding: 4,
             }}>
          <button type="button" role="menuitem" data-testid="download-print"
                  disabled={busy !== null} onClick={() => run('print')}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                           textAlign: 'left', padding: '8px 12px', fontSize: 12.5,
                           border: 'none', background: 'none', cursor: 'pointer', color: C.body }}>
            <Printer size={13} aria-hidden="true" /> Print / Save as PDF
          </button>
          <div style={{ height: 1, background: C.borderSoft, margin: '4px 0' }} />
          {item('pdf', 'Download PDF')}
          {item('docx', 'Download Word (.docx)')}
          {item('xlsx', 'Download Excel (.xlsx)')}
          {!canFinance ? (
            /* ⚠ Said up front, not discovered in the file. */
            <div style={{ ...S.kpiSub, padding: '6px 12px' }}>
              Revenue and receivables are omitted — they need finance access.
            </div>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <div data-testid="download-error" role="alert"
             style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, zIndex: 50,
                      background: C.highBg, color: C.highDeep, border: `1px solid ${C.high}`,
                      borderRadius: 8, padding: '8px 12px', fontSize: 12, minWidth: 240 }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}
